import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';

interface Dependent {
  id: string;
  name: string;
  nameKana: string;
  relationship: string;
  birthDate: string;
  gender: string;
  income: number;
  livingTogether: boolean;
  disability: string;
  myNumber: string;
  createdAt: string;
  updatedAt: string;
}

const relationshipOptions = [
  { value: 'spouse', label: '配偶者' },
  { value: 'child', label: '子' },
  { value: 'parent', label: '父・母' },
  { value: 'grandparent', label: '祖父・祖母' },
  { value: 'grandchild', label: '孫' },
  { value: 'sibling', label: '兄弟・姉妹' },
  { value: 'other', label: 'その他' },
];

const disabilityOptions = [
  { value: '', label: 'なし' },
  { value: 'general', label: '一般障害者' },
  { value: 'special', label: '特別障害者' },
  { value: 'living_special', label: '同居特別障害者' },
];

const initialFormData = {
  name: '',
  nameKana: '',
  relationship: '',
  birthDate: '',
  gender: '',
  income: 0,
  livingTogether: true,
  disability: '',
  myNumber: '',
};

export default function DependentManagement() {
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedDependent, setSelectedDependent] = useState<Dependent | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchDependents();
  }, []);

  const fetchDependents = async () => {
    setLoading(true);
    try {
      const data = await api.get<{ items: Dependent[] }>('/api/dependents');
      setDependents(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch dependents:', error);
      setDependents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (dependent: Dependent) => {
    setSelectedDependent(dependent);
    setFormData({
      name: dependent.name,
      nameKana: dependent.nameKana || '',
      relationship: dependent.relationship,
      birthDate: dependent.birthDate,
      gender: dependent.gender || '',
      income: dependent.income || 0,
      livingTogether: dependent.livingTogether,
      disability: dependent.disability || '',
      myNumber: dependent.myNumber || '',
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.relationship || !formData.birthDate || submitting) return;

    setSubmitting(true);
    try {
      if (isEditing && selectedDependent) {
        await api.put(`/api/dependents/${selectedDependent.id}`, formData);
      } else {
        await api.post('/api/dependents', formData);
      }
      setShowModal(false);
      setFormData(initialFormData);
      setSelectedDependent(null);
      fetchDependents();
    } catch (error) {
      console.error('Failed to save dependent:', error);
      toast.error('保存に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDependent) return;

    try {
      await api.delete(`/api/dependents/${selectedDependent.id}`);
      setShowDeleteConfirm(false);
      setSelectedDependent(null);
      fetchDependents();
    } catch (error) {
      console.error('Failed to delete dependent:', error);
      toast.error('削除に失敗しました');
    }
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  const getRelationshipLabel = (value: string) => {
    return relationshipOptions.find(opt => opt.value === value)?.label || value;
  };

  const getDisabilityLabel = (value: string) => {
    return disabilityOptions.find(opt => opt.value === value)?.label || 'なし';
  };

  const isEligibleForDeduction = (dependent: Dependent) => {
    // 所得が48万円以下なら控除対象
    return (dependent.income || 0) <= 480000;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">扶養家族管理</h1>
          <p className="text-sm text-gray-500">年末調整や給与計算に使用される扶養家族情報を管理します</p>
        </div>
        <Button onClick={handleAdd} className="w-full sm:w-auto">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          扶養家族を追加
        </Button>
      </div>

      {/* 注意事項 */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium">扶養控除の対象となる条件</p>
            <ul className="list-disc list-inside mt-1 text-blue-700 dark:text-blue-300">
              <li>年間所得が48万円以下であること</li>
              <li>生計を一にしていること</li>
              <li>16歳以上（扶養控除）または16歳未満（年少扶養）</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* 扶養家族一覧 */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : dependents.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-gray-500 mb-4">扶養家族が登録されていません</p>
            <Button onClick={handleAdd}>扶養家族を追加</Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dependents.map(dependent => {
            const age = calculateAge(dependent.birthDate);
            const eligible = isEligibleForDeduction(dependent);
            return (
              <Card key={dependent.id} className="hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{dependent.name}</h3>
                      {dependent.nameKana && (
                        <p className="text-xs text-gray-500">{dependent.nameKana}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {eligible ? (
                      <Badge variant="success">控除対象</Badge>
                    ) : (
                      <Badge variant="default">対象外</Badge>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div>
                    <p className="text-gray-500">続柄</p>
                    <p className="font-medium text-gray-900 dark:text-white">{getRelationshipLabel(dependent.relationship)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">年齢</p>
                    <p className="font-medium text-gray-900 dark:text-white">{age}歳</p>
                  </div>
                  <div>
                    <p className="text-gray-500">生年月日</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {new Date(dependent.birthDate).toLocaleDateString('ja-JP')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">年間所得</p>
                    <p className={`font-medium ${(dependent.income || 0) > 480000 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                      {formatCurrency(dependent.income || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">同居</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {dependent.livingTogether ? 'はい' : 'いいえ'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">障害</p>
                    <p className="font-medium text-gray-900 dark:text-white">{getDisabilityLabel(dependent.disability)}</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t dark:border-gray-700">
                  <Button variant="secondary" size="sm" className="flex-1" onClick={() => handleEdit(dependent)}>
                    編集
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedDependent(dependent); setShowDeleteConfirm(true); }}>
                    削除
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* 追加・編集モーダル */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setFormData(initialFormData); setSelectedDependent(null); }}
        title={isEditing ? '扶養家族を編集' : '扶養家族を追加'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="氏名"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <Input
              label="氏名（フリガナ）"
              name="nameKana"
              value={formData.nameKana}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="続柄"
              name="relationship"
              value={formData.relationship}
              onChange={handleChange}
              required
            >
              <option value="">選択してください</option>
              {relationshipOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
            <Input
              label="生年月日"
              name="birthDate"
              type="date"
              value={formData.birthDate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="性別"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <option value="">選択してください</option>
              <option value="male">男性</option>
              <option value="female">女性</option>
            </Select>
            <Input
              label="年間所得（見込み）"
              name="income"
              type="number"
              value={formData.income || ''}
              onChange={handleChange}
              placeholder="円"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="livingTogether"
                  checked={formData.livingTogether}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">同居している</span>
              </label>
            </div>
            <Select
              label="障害区分"
              name="disability"
              value={formData.disability}
              onChange={handleChange}
            >
              {disabilityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </div>

          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">マイナンバー（個人番号）</p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  マイナンバーは暗号化して安全に保存されます。年末調整・給与計算に使用されます。
                </p>
              </div>
            </div>
            <Input
              name="myNumber"
              type="password"
              value={formData.myNumber}
              onChange={handleChange}
              placeholder="12桁の数字"
              className="mt-3"
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => { setShowModal(false); setFormData(initialFormData); setSelectedDependent(null); }}
              className="w-full sm:w-auto"
            >
              キャンセル
            </Button>
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? '保存中...' : isEditing ? '更新' : '追加'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setSelectedDependent(null); }}
        title="削除の確認"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            <span className="font-medium">{selectedDependent?.name}</span> さんを削除しますか？
          </p>
          <p className="text-sm text-gray-500">この操作は取り消せません。</p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
              キャンセル
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              削除
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

